/**
 * Dashboard Type Definitions
 *
 * Types for the admin dashboard export statistics.
 * Matches the response shape from GET /admin/export-stats endpoint.
 */

// ============================================
// EXPORT STATS (from /admin/export-stats)
// ============================================

export interface AttorneyExportStats {
  prof_id: string;
  name: string;
  email: string;
  exported_hours: number;
  unexported_hours: number;
  total_hours: number;
  exported_pct: number;
}

export interface ExportStatsTotals {
  exported_hours: number;
  unexported_hours: number;
  total_hours: number;
  exported_pct: number;
  unexported_pct: number;
}

export interface ExportStats {
  startDate: string | null;
  endDate: string | null;
  attorneys: AttorneyExportStats[];
  totals: ExportStatsTotals;
  metrics: {
    exportedHours: number;
    unexportedHours: number;
    deletedHours: number;
    activeUsers: number;
  };
}

// ============================================
// TIMEFRAME OPTIONS
// ============================================

export interface TimeframeOption {
  value: string;
  label: string;
}

export const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "3days", label: "Last 3 Days" },
  { value: "7days", label: "Last 7 Days" },
  { value: "thismonth", label: "This Month" },
  { value: "30days", label: "Last 30 Days" },
  { value: "90days", label: "Last 90 Days" },
  { value: "1year", label: "Last Year" },
  { value: "all", label: "All Time" },
];

export function getTimeframeLabel(timeframe: string): string {
  return TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)?.label ?? "Today";
}

/**
 * Converts a timeframe string to start and end dates.
 * Used to call the backend API with date range parameters.
 */
// ============================================
// YEAR-OVER-YEAR (Tab 1)
// ============================================

export type YoYGranularity = "weekly" | "monthly" | "daily";

export interface GoLiveRecord {
  prof_id: string;
  go_live_date: string; // "YYYY-MM-DD"
}

export interface YoYPeriodSummary {
  total_billed: number;
  total_hours: number;
  weeks: number;
  months: number;
  avg_weekly_billed: number;
  avg_weekly_hours: number;
  avg_monthly_billed: number;
  avg_monthly_hours: number;
}

export interface YoYSummary {
  before: YoYPeriodSummary | null;
  after: YoYPeriodSummary | null;
  goLiveDate: string | null;
  afterStart: string | null;
  afterEnd: string;
  beforeStart: string | null;
  beforeEnd: string | null;
}

export interface YoYDataPoint {
  year: number;
  period_num: number;
  prof_id: string;
  prof_name: string;
  hours: number;
  billed: number;
  period: "Before" | "After";
}

// ============================================
// UTILIZATION (Hourglass Usage Tab)
// ============================================

export interface AttorneyUtilization {
  prof_id: string;
  prof_name: string;
  hourglass_hours: number;
  total_billing_hours: number;
  utilization_pct: number;
  unexported_hours: number;
  deleted_hours: number;
}

export interface UtilizationTotals {
  hourglass_hours: number;
  total_billing_hours: number;
  utilization_pct: number;
  unexported_hours: number;
  deleted_hours: number;
  attorney_count: number;
}

export interface UtilizationData {
  attorneys: AttorneyUtilization[];
  totals: UtilizationTotals;
  billing_last_synced: string | null;
}

// ============================================
// TIMEKEEPER PERFORMANCE (Tab 2)
// ============================================

export interface AvailableWeek {
  week_start: string; // YYYY-MM-DD
  week_end: string;
}

export interface PerformanceRow {
  prof_id: string;
  prof_name: string;
  hg_week: number;
  utilization_pct: number;
  curr_week: number;
  avg_week: number;
  curr_month: number;
  avg_month: number;
  curr_ytd: number;
  prior_ytd: number;
}

export interface PerformanceData {
  rows: PerformanceRow[];
  as_of_label: string; // "MM/DD/YYYY"
  week_label: string; // "MM/DD - MM/DD"
}

// ============================================
// DAILY VIEW (Tab 5)
// ============================================

export interface DailyDataPoint {
  date: string;  // YYYY-MM-DD
  hours: number;
}

export interface DailyViewData {
  after: DailyDataPoint[];   // daily hours from go-live to today
  before: DailyDataPoint[];  // daily hours from go-live-1yr to today-1yr
  goLiveDate: string | null;
}

// ============================================
// EXPERIMENTAL (Weekly Hours Trend)
// ============================================

export interface ExperimentalWeekPoint {
  week_start: string;  // YYYY-MM-DD
  week_end: string;    // YYYY-MM-DD
  week_label: string;  // "3/01-3/08"
  hours: number;
  hg_hours: number;
  utilization_pct: number;  // 0–100
}

export interface ExperimentalProfData {
  prof_name: string;
  prof_id: string;
  pre_hg_avg: number;
  pre_hg_std: number;
  pre_hg_period_label: string;
  weekly_hours: ExperimentalWeekPoint[];
}

export interface ExperimentalData {
  professionals: ExperimentalProfData[];
}

// ============================================
// TIMEFRAME HELPERS
// ============================================

export function getDateRangeFromTimeframe(timeframe: string): {
  startDate: string;
  endDate: string;
} {
  const today = new Date();
  const endDate = today.toISOString().split("T")[0];

  let startDate: string;

  switch (timeframe) {
    case "today":
      startDate = endDate;
      break;
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = yesterday.toISOString().split("T")[0];
      return { startDate, endDate: startDate }; // Both dates are yesterday
    }
    case "3days": {
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
      startDate = threeDaysAgo.toISOString().split("T")[0];
      break;
    }
    case "7days": {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6);
      startDate = weekAgo.toISOString().split("T")[0];
      break;
    }
    case "thismonth": {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate = firstOfMonth.toISOString().split("T")[0];
      break;
    }
    case "30days": {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
      startDate = thirtyDaysAgo.toISOString().split("T")[0];
      break;
    }
    case "90days": {
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 89);
      startDate = ninetyDaysAgo.toISOString().split("T")[0];
      break;
    }
    case "1year": {
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      startDate = yearAgo.toISOString().split("T")[0];
      break;
    }
    case "all":
      // Use a very old date for "all time"
      startDate = "2020-01-01";
      break;
    default:
      startDate = endDate; // Default to today
  }

  return { startDate, endDate };
}
